import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { IVenue } from 'models/i-venue.interface';
import { IState } from 'models/reducers/i-state.interface';

import { ROUTES } from 'constants/routes.constant';

import { useMenuVisibility } from 'contexts/MenuVisibilityContext';

import { addInfoMessageAction } from 'actions/global-actions';

import { useHomePageNavigate } from 'hooks/useHomePageNavigate';

import { isRoute } from 'utils/route.utils';

import styles from './Header.module.scss';
import HeaderDesktop from './HeaderDesktop';
import HeaderMobile from './HeaderMobile';

interface IHeader {
  className?: string;
  // Optional visual variant, defaults to auto-detected from route
  variant?: 'default' | 'referral';
}

const Header = ({ className, variant }: IHeader) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { homePage, mapPageDisabled } = useHomePageNavigate();
  const { hamburgerMenuVisible: menuVisible, setHamburgerMenuVisible, toggleHamburgerMenu } = useMenuVisibility();
  const activeVenue = useSelector((state: IState) => state.venuesActive.activeVenue) as IVenue;
  const searchData = useSelector((state: IState) => state.search.search);

  // Close menu when route changes
  useEffect(() => {
    setHamburgerMenuVisible(false);
  }, [location, setHamburgerMenuVisible]);

  // Close menu on ESC key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && menuVisible) {
        setHamburgerMenuVisible(false);
      }
    };

    document.addEventListener('keydown', handleEscape, false);
    return () => document.removeEventListener('keydown', handleEscape, false);
  }, [menuVisible, setHamburgerMenuVisible]);

  // Variant autodetect if not provided
  const computedVariant: 'default' | 'referral' = useMemo(() => {
    if (variant) return variant;

    const isReferralVariant =
      isRoute({
        pathname: location.pathname,
        routes: [ROUTES.REFERRAL, ROUTES.AMA, ROUTES.INFORMATIONAL, ROUTES.TASK_BASED, ROUTES.BID2EARN, ROUTES.BURN],
        startsWith: true,
      }) ||
      isRoute({
        pathname: location.pathname,
        routes: [ROUTES.EVENTS, ROUTES.PERIODICAL_STAKING, ROUTES.VIP, ROUTES.DASHBOARD],
        trailingSlash: true,
      });

    return isReferralVariant ? 'referral' : 'default';
  }, [variant, location]);

  const mapUrl = useMemo((): string => {
    if (mapPageDisabled) return homePage;

    let url = ROUTES.PORTAL;
    if (activeVenue) url += `/${activeVenue.type}/${activeVenue.id}`;
    if (searchData) url += `?s=${searchData}`;
    return url;
  }, [mapPageDisabled, homePage, activeVenue, searchData]);

  /**
   * Show informational message when user tries to access disabled map
   */
  const handleMapDisabledClick = (): void => {
    if (mapPageDisabled) {
      dispatch(addInfoMessageAction('Open website on desktop to enable map functionality'));
    }
  };

  return (
    <header className={`${className || ''} ${styles['menu']} ${styles[`menu--${computedVariant}`]}`}>
      <HeaderMobile
        menuVisible={menuVisible}
        mapPageDisabled={mapPageDisabled}
        mapUrl={mapUrl}
        variant={computedVariant}
        onToggleMenu={toggleHamburgerMenu}
        onCloseMenu={() => setHamburgerMenuVisible(false)}
        onMapDisabledClick={handleMapDisabledClick}
      />

      <HeaderDesktop
        mapPageDisabled={mapPageDisabled}
        mapUrl={mapUrl}
        variant={computedVariant}
        onMapDisabledClick={handleMapDisabledClick}
      />
    </header>
  );
};

export default Header;
